import { useEffect, useState } from "react";
import { Eye, MousePointerClick } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function Suivi({ token, setError }) {
  const [views, setViews] = useState(null);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    const headers = { Authorization: `Bearer ${token}` };
    Promise.all([
      fetch(`${API}/api/admin/views`, { headers }),
      fetch(`${API}/api/admin/views/summary`, { headers }),
    ])
      .then(async ([r1, r2]) => {
        if (!r1.ok || !r2.ok) throw new Error("failed");
        setViews((await r1.json()).views);
        setSummary((await r2.json()).summary);
      })
      .catch(() => setError("Chargement impossible."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="suivi-panel" className="mt-2">
      {/* Aggregated analytics */}
      <h2 className="text-[11px] font-medium uppercase tracking-[0.25em] text-champagne">
        Synthèse — qui consulte quoi
      </h2>
      {!summary ? (
        <p className="mt-8 text-ivory/50">Chargement…</p>
      ) : summary.length === 0 ? (
        <p data-testid="suivi-summary-empty" className="mt-8 text-ivory/50">
          Aucune consultation pour le moment.
        </p>
      ) : (
        <div className="mt-6 divide-y divide-ivory/10 border-y border-ivory/10" data-testid="suivi-summary">
          {summary.map((s, i) => (
            <div
              key={`${s.member_id}-${s.property_id}`}
              data-testid={`suivi-row-${i}`}
              className="grid items-baseline gap-2 py-5 md:grid-cols-[240px_1fr_120px_180px] md:gap-8"
            >
              <div>
                <p className="text-sm text-ivory">{s.member_name}</p>
                <p className="text-xs text-ivory/45">{s.member_company || s.member_email}</p>
              </div>
              <p className="text-sm font-light italic text-ivory/65">{s.property_title}</p>
              <p className="flex items-center gap-2" data-testid={`suivi-clicks-${i}`}>
                <MousePointerClick size={14} className="text-champagne" />
                <span className="font-serif text-2xl font-medium text-ivory">{s.clicks}</span>
                <span className="text-[10px] uppercase tracking-[0.18em] text-ivory/40">clics</span>
              </p>
              <p className="text-xs tabular-nums text-ivory/40 md:text-right">
                {new Date(s.last_viewed_at).toLocaleString("fr-CH", { dateStyle: "medium", timeStyle: "short" })}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Chronological log */}
      <h2 className="mt-16 text-[11px] font-medium uppercase tracking-[0.25em] text-ivory/40">
        Journal détaillé {views ? `(${views.length})` : ""}
      </h2>
      {!views ? (
        <p className="mt-8 text-ivory/50">Chargement…</p>
      ) : views.length === 0 ? (
        <p data-testid="suivi-empty" className="mt-8 text-ivory/50">
          Les vues des objets off-market apparaissent ici.
        </p>
      ) : (
        <div className="mt-6 divide-y divide-ivory/10 border-y border-ivory/10" data-testid="suivi-list">
          {views.map((v) => (
            <div key={v.id} data-testid={`suivi-view-${v.id}`} className="flex flex-wrap items-baseline gap-x-8 gap-y-1 py-4">
              <span className="text-xs tabular-nums text-ivory/40">
                {new Date(v.viewed_at).toLocaleString("fr-CH", { dateStyle: "medium", timeStyle: "short" })}
              </span>
              <span className="flex items-center gap-2 text-sm text-ivory">
                <Eye size={13} className="text-bronze" />
                {v.member_name}
                <span className="text-xs text-ivory/45">
                  {v.member_company} · {v.member_email}
                </span>
              </span>
              <span className="text-sm font-light italic text-ivory/60">→ {v.property_title}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
